/**
 * UsageProgressBar Component
 * Reusable usage progress bar for SKU count and SmartImport runs
 *
 * Shows:
 * - Label (e.g., "SKUs", "SmartImport Runs")
 * - Current / Limit counts
 * - Progress bar with color coding (green/yellow/red)
 * - "Unlimited" indicator if applicable
 */

import { Progress, Space, Typography } from 'antd'
import { CheckCircleOutlined } from '@ant-design/icons'

const { Text } = Typography

interface Props {
  label: string
  current: number
  limit: number | null
  percentage: number
  unlimited?: boolean
  showWarning?: boolean
}

export function UsageProgressBar({
  label,
  current,
  limit,
  percentage,
  unlimited = false,
  showWarning = false
}: Props) {
  // Determine progress bar color
  const progressColor = unlimited
    ? 'var(--rp-success)'  // Green for unlimited
    : percentage > 90
    ? 'var(--rp-temporal-red)'  // Red if >90%
    : percentage > 70
    ? 'var(--rp-temporal-orange)'  // Orange if 70-90%
    : 'var(--rp-success)'  // Green if <70%

  if (unlimited) {
    return (
      <Space direction="horizontal" style={{ width: '100%', justifyContent: 'space-between' }}>
        <Text>{label}</Text>
        <Space>
          <CheckCircleOutlined style={{ color: 'var(--rp-success)' }} />
          <Text strong style={{ color: 'var(--rp-success)' }}>Unlimited</Text>
        </Space>
      </Space>
    )
  }

  return (
    <div>
      <Space direction="horizontal" style={{ width: '100%', justifyContent: 'space-between', marginBottom: '4px' }}>
        <Text>{label}</Text>
        <Text>
          <span style={{ fontWeight: showWarning ? 600 : 400 }}>
            {current.toLocaleString()}
          </span>
          {' / '}
          {limit !== null ? limit.toLocaleString() : 'Unlimited'}
        </Text>
      </Space>
      {limit !== null && (
        <Progress
          percent={Math.min(percentage, 100)}
          strokeColor={progressColor}
          showInfo={false}
          size="small"
        />
      )}
    </div>
  )
}
